import { useTranslation } from 'react-i18next';
import { LegacyRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typo } from '../Typo';
import { Icon } from '../Icon';
import { Pagination, Table } from '../Table';
import { Button } from '../Button';
import { throttle } from 'app/helpers';
import {
  AreaTargetCatalogOrderParams,
  AreaTargetCatalogSearchRequest,
  PortTargetCatalogOrderParams,
  PortTargetCatalogRequest,
  WithinDistanceTargetCatalogRequest,
  WithinDistanceTargetCatalogOrderCriteriaEnum,
  AreaTargetCatalogOrderCriteriaEnum,
  PortTargetCatalogOrderCriteriaEnum,
  WithinDistanceTargetCatalogOrderParams,
  OrderDirectionEnum,
} from 'ais-facade-api/api';

import styles from './VesselsCatalogModal.module.scss';

interface Props {
  columns: any;
  requestParam: string | number;
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  titles: {
    title: string;
    titleTotal: string;
  };
  vesselsDataMutationResult: any;
  defaultSortBy:
    | WithinDistanceTargetCatalogOrderCriteriaEnum
    | AreaTargetCatalogOrderCriteriaEnum
    | PortTargetCatalogOrderCriteriaEnum;
  defaultSortDirection?: OrderDirectionEnum;
}

export interface TargetCatalogSortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: string;
}

export const VesselsCatalogModal = ({
  innerRef,
  requestParam,
  onClose,
  vesselsDataMutationResult,
  titles,
  columns,
  defaultSortBy,
  defaultSortDirection,
}: Props) => {
  const initialSortDirection = defaultSortDirection || 'DESC';
  const initialSort: AreaTargetCatalogOrderParams | PortTargetCatalogOrderParams | WithinDistanceTargetCatalogOrderParams = {
    direction: initialSortDirection,
    by: defaultSortBy,
  };
  const container = useRef<HTMLDivElement>(null);
  const [getVesselsData, vesselsDataState] = vesselsDataMutationResult;
  const [total, setTotal] = useState<number>(0);

  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);
  const [sorting, setSorting] = useState<TargetCatalogSortingInterface>(initialSort);
  const [currentData, setCurrentData] = useState<any[]>();

  const debouncedGetVesselsData = useCallback(throttle(getVesselsData, 200), []);

  const handleClose = () => {
    onClose();
    vesselsDataState?.reset();
  };

  const initialBody: AreaTargetCatalogSearchRequest | PortTargetCatalogRequest | WithinDistanceTargetCatalogRequest = {
    filter: {},
    order: {
      direction: 'DESC',
      by: defaultSortBy,
    },
    pagination: {
      offset: 0,
      limit: 20,
    },
  };

  useEffect(() => {
    debouncedGetVesselsData({
      requestParam,
      body: {
        ...initialBody,
        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      },
    });
  }, [currentOffset]);

  useEffect(() => {
    setCurrentPage(1);

    debouncedGetVesselsData({
      requestParam,
      body: {
        ...initialBody,
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      },
    });
  }, [sorting]);
  useEffect(() => {
    if (vesselsDataState.data && vesselsDataState.data.results && !vesselsDataState.isLoading) {
      setCurrentData(vesselsDataState.data.results);
    }
  }, [vesselsDataState.data, vesselsDataState.isLoading]);
  useEffect(() => {
    if (vesselsDataState.data?.total) {
      setTotal(vesselsDataState.data?.total);
    }
  }, [vesselsDataState.data?.total]);
  return (
    <div className={styles.root}>
      <div className={styles.wrapper} ref={innerRef}>
        <div className={styles.panel_header}>
          <Typo size="m" value={t(titles.title)} />
          <Icon name="close-16" size="16" onClick={handleClose} />
        </div>
        {currentData && currentData.length !== 0 && (
          <Typo className={styles.total} variant="div" size="m" value={`${t(titles.titleTotal)} : ${total}`} />
        )}

        <div className={styles.dialog_modal} ref={innerRef}>
          <div className={styles.table_container} ref={container}>
            {!vesselsDataState.isLoading && currentData?.length === 0 && (
              <Typo variant="p" size="m" className={styles.no_data} value={t('NoResultsFound')} />
            )}
            {vesselsDataState.isUninitialized && (
              <Typo variant="p" size="m" className={styles.no_data} value={t('DataIsLoading')} />
            )}
            {currentData && currentData.length !== 0 && (
              <Table manualSort={true} sorting={sorting} setSorting={setSorting} columns={columns} data={currentData} />
            )}
          </div>

          <div className={styles.pagination_container}>
            <Pagination totalCount={total} currentPage={currentPage} pageChangeHandler={setCurrentPage} rowsPerPage={20} />
            <div className={styles.actions}>
              <Button
                className={styles.action_button}
                type="button"
                label={t('ButtonLabelClose')}
                variant="general"
                onClick={handleClose}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
